import {
  useMemo,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FC,
} from "react";
import type { TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";

import "../TransactionsTable/stylesTransactionTable.css";
import PaymentsFilters from "./PaymentsFilters";
import { PaymentDetailsDrawer } from "../../../shared";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { getAdminNow } from "../../../../utils/dateService";
import DataTableUntyped from "../../../shared/dashboards/DataTable/DataTable";
import useExternalTableFilters from "../../../shared/table-filters/useExternalTableFilters";
import {
  resolveDateRangeParams,
  resolveServerSorter,
} from "../../../shared/table-filters/filterQueryUtils";
import {
  applyPaymentQuery,
  type PaymentDirectionFilter,
  type PaymentSortBy,
  type PaymentSortOrder,
} from "./applyPaymentQuery";
import {
  buildMockPayments,
  computeMockPaymentTotals,
  type PaymentRow,
} from "./mockPayments";
import { getPaymentsTableColumns } from "./paymentsTableColumns";

type PaymentsTableProps = {
  userData: { user_id?: string; username?: string } | null | undefined;
  adminData?: { timezone?: string } | null;
  currencySymbol?: string;
};

type PaymentFilters = {
  searchValue: string;
  direction: PaymentDirectionFilter;
  minPayment: string;
  maxPayment: string;
};

type AppliedPaymentFilters = PaymentFilters;

type PaginationState = {
  current_page: number;
  page_size: number;
  total_results: number;
};

type FiltersHookResult = {
  filters: PaymentFilters;
  normalizedFilters: PaymentFilters;
  setFilter: <K extends keyof PaymentFilters>(
    key: K,
    value: PaymentFilters[K],
  ) => void;
  resetFilters: () => void;
};

type PaymentsDataTableProps = {
  columns: ColumnsType<PaymentRow>;
  dataSource: PaymentRow[];
  pagination:
    | false
    | {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, size: number) => void;
        showSizeChanger?: boolean;
        pageSizeOptions?: readonly string[] | string[];
        showQuickJumper?: boolean;
        hideOnSinglePage?: boolean;
      };
  scrollPreset?: string;
  scrollOverrides?: { x?: number };
  rowKey?: (record: PaymentRow) => string | undefined;
  onRow?: (record: PaymentRow) => {
    onClick?: () => void;
    className?: string;
    style?: CSSProperties;
  };
  onChange?: TableProps<PaymentRow>["onChange"];
};

const TypedDataTable =
  DataTableUntyped as unknown as FC<PaymentsDataTableProps>;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS: readonly string[] = ["10", "20", "50", "100"];

const EMPTY_APPLIED: AppliedPaymentFilters = {
  searchValue: "",
  direction: "both",
  minPayment: "",
  maxPayment: "",
};

const PaymentsTable: FC<PaymentsTableProps> = ({
  userData,
  currencySymbol,
}) => {
  const allPayments = useMemo(() => buildMockPayments(getAdminNow()), []);
  const summary = useMemo(
    () => computeMockPaymentTotals(allPayments),
    [allPayments],
  );

  const {
    filters,
    normalizedFilters,
    setFilter,
    resetFilters,
  } = useExternalTableFilters({
    searchValue: "",
    direction: "both",
    minPayment: "",
    maxPayment: "",
  }) as unknown as FiltersHookResult;

  const [appliedFilters, setAppliedFilters] =
    useState<AppliedPaymentFilters>(EMPTY_APPLIED);
  const [paymentDates, setPaymentDates] = useState<[Dayjs, Dayjs] | null>(
    null,
  );
  const [sortBy, setSortBy] = useState<PaymentSortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<PaymentSortOrder | null>(null);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState<PaginationState>({
    current_page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_results: allPayments.length,
  });
  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState(false);
  const [selectedPaymentTransactionId, setSelectedPaymentTransactionId] =
    useState<string | null>(null);

  const dateRange = useMemo(() => {
    if (!paymentDates) {
      return { startDate: null as string | null, endDate: null as string | null };
    }
    return resolveDateRangeParams({
      queryString: paymentDates,
      isQueryString: false,
      allTime: false,
    });
  }, [paymentDates]);

  const queryResult = useMemo(
    () =>
      applyPaymentQuery(allPayments, {
        searchValue: appliedFilters.searchValue,
        direction: appliedFilters.direction,
        minPayment: appliedFilters.minPayment,
        maxPayment: appliedFilters.maxPayment,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy,
        sortOrder,
        page: pagination.current_page,
        pageSize,
      }),
    [
      allPayments,
      appliedFilters,
      dateRange.endDate,
      dateRange.startDate,
      pageSize,
      pagination.current_page,
      sortBy,
      sortOrder,
    ],
  );

  const payments = queryResult.items;
  const totalResults = queryResult.total;

  const columns = useMemo(
    () =>
      getPaymentsTableColumns({
        currencySymbol,
        sortBy,
        sortOrder,
      }),
    [currencySymbol, sortBy, sortOrder],
  );

  const openPaymentDetails = (record: PaymentRow) => {
    setSelectedPaymentTransactionId(record?.transaction_id ?? null);
    setIsPaymentDetailsOpen(true);
  };

  const closePaymentDetails = () => {
    setIsPaymentDetailsOpen(false);
    setSelectedPaymentTransactionId(null);
  };

  const commitFilters = (next: AppliedPaymentFilters) => {
    setAppliedFilters(next);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearchValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setFilter("searchValue", event.target.value);
  };

  const handleDirectionChange = (
    value: PaymentDirectionFilter | undefined,
  ): void => {
    setFilter("direction", value ?? "both");
  };

  const handleMinPaymentChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setFilter("minPayment", event.target.value);
  };

  const handleMaxPaymentChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setFilter("maxPayment", event.target.value);
  };

  const handleApplyFilters = (): void => {
    commitFilters({
      searchValue: "",
      direction: normalizedFilters.direction ?? "both",
      minPayment: normalizedFilters.minPayment ?? "",
      maxPayment: normalizedFilters.maxPayment ?? "",
    });
    setFilter("searchValue", "");
  };

  const handleSearchPressed = (): void => {
    const trimmedSearchValue = (normalizedFilters.searchValue ?? "").trim();
    if (!trimmedSearchValue) {
      return;
    }
    commitFilters({
      ...appliedFilters,
      searchValue: trimmedSearchValue,
    });
  };

  const handleSearchClear = (): void => {
    if (!appliedFilters.searchValue) {
      return;
    }
    commitFilters({
      ...appliedFilters,
      searchValue: "",
    });
  };

  const handleClearFilters = (): void => {
    resetFilters();
    setPaymentDates(null);
    setSortBy(null);
    setSortOrder(null);
    setAppliedFilters(EMPTY_APPLIED);
    setPagination({
      current_page: 1,
      page_size: pageSize,
      total_results: allPayments.length,
    });
  };

  const handleDateChange = (): void => {
    resetFilters();
    setAppliedFilters(EMPTY_APPLIED);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handlePageSizeChange = (nextPageSize: number): void => {
    if (nextPageSize === pageSize) return;
    setPageSize(nextPageSize);
    setPagination({
      current_page: 1,
      page_size: nextPageSize,
      total_results: totalResults,
    });
  };

  const handlePaginationChange = (
    nextPage: number,
    nextSize: number,
  ): void => {
    if (nextSize !== pageSize) {
      handlePageSizeChange(nextSize);
      return;
    }
    setPagination({
      current_page: nextPage,
      page_size: pageSize,
      total_results: totalResults,
    });
  };

  const handleTableChange: NonNullable<TableProps<PaymentRow>["onChange"]> = (
    _nextPagination,
    _filters,
    sorter,
  ) => {
    const { sortBy: nextSortBy, sortOrder: nextSortOrder } =
      resolveServerSorter(sorter);

    if (nextSortBy === sortBy && nextSortOrder === sortOrder) {
      return;
    }

    const isSortableColumn =
      nextSortBy === "amount" || nextSortBy === "total_balance";

    if (isSortableColumn && nextSortOrder) {
      setSortBy(nextSortBy);
      setSortOrder(nextSortOrder as PaymentSortOrder);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      return;
    }

    if (sortBy || sortOrder) {
      setSortBy(null);
      setSortOrder(null);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  };

  const headerText = formatString(AppStrings.PAYMENT_USERNAME_PAYMENTS, {
    username: userData?.username ?? "",
  });

  const isDirectionActive = (value: PaymentDirectionFilter | undefined) =>
    Boolean(value) && value !== "both";

  const hasAppliedOrDraftFilters =
    Boolean(filters.searchValue.trim()) ||
    isDirectionActive(filters.direction) ||
    Boolean(filters.minPayment) ||
    Boolean(filters.maxPayment) ||
    Boolean(appliedFilters.searchValue) ||
    isDirectionActive(appliedFilters.direction) ||
    Boolean(appliedFilters.minPayment) ||
    Boolean(appliedFilters.maxPayment);

  return (
    <div
      className="transactions-user-wallet-container"
      data-testid="payments-table"
    >
      <PaymentDetailsDrawer
        open={isPaymentDetailsOpen}
        onClose={closePaymentDetails}
        userId={userData?.user_id}
        transactionId={selectedPaymentTransactionId}
        currencySymbol={currencySymbol}
      />
      <div className="transactions-user-wallet-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={false}
            title={AppStrings.TOTAL_PURCHASED}
            value={summary.total_purchased}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={false}
            title={AppStrings.TOTAL_WITHDRAWN}
            value={summary.total_withdrawn}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={false}
            title={AppStrings.REVENUE}
            value={summary.total_purchased - summary.total_withdrawn}
            titleTooltip={AppStrings.REVENUE_KPI_TOOLTIP}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={paymentDates}
          setDates={setPaymentDates}
          showPresets={true}
          startRange={AppStrings.TODAY}
        />
      </div>
      <div className="transactions-user-wallet-table-container">
        <PaymentsFilters
          headerText={headerText}
          searchValue={filters.searchValue}
          direction={filters.direction}
          minPayment={filters.minPayment}
          maxPayment={filters.maxPayment}
          onSearchValueChange={handleSearchValueChange}
          onSearchClear={handleSearchClear}
          onSearchPressed={handleSearchPressed}
          onDirectionChange={handleDirectionChange}
          onMinPaymentChange={handleMinPaymentChange}
          onMaxPaymentChange={handleMaxPaymentChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          hasAppliedOrDraftFilters={hasAppliedOrDraftFilters}
        />

        <div className="transactions-table-container">
          <TypedDataTable
            columns={columns}
            dataSource={payments}
            scrollPreset="default"
            scrollOverrides={{ x: 2050 }}
            rowKey={(record) => record?.transaction_id}
            onChange={handleTableChange}
            onRow={(record) =>
              record?.is_verification
                ? {}
                : {
                    onClick: () => openPaymentDetails(record),
                    className: "transaction-table-row-clickable",
                    style: { cursor: "pointer" },
                  }
            }
            pagination={
              totalResults > pageSize
                ? {
                    current: pagination.current_page,
                    pageSize,
                    total: totalResults,
                    onChange: (nextPage, nextSize) =>
                      handlePaginationChange(nextPage, nextSize),
                    showSizeChanger: true,
                    pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                    showQuickJumper: false,
                    hideOnSinglePage: true,
                  }
                : false
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentsTable;
